import React from 'react';
import {
  Box,
  IconButton,
  TextField,
  InputAdornment,
  ListItem,
  ListItemIcon,
} from '@mui/material';
import { DragIndicator, Delete as DeleteIcon } from '@mui/icons-material';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

function TierRow({ tier, tierIndex, onUpdate, onRemove, canDelete, validationErrors }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: tier.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.8 : 1,
  };

  const tierErrors = tierIndex != null && validationErrors?.tiers?.[tierIndex] ? validationErrors.tiers[tierIndex] : {};
  const nameError = tierErrors?.name;
  const labelError = tierErrors?.label;

  return (
    <ListItem
      ref={setNodeRef}
      style={style}
      sx={{
        mb: 1,
        backgroundColor: isDragging ? '#e3f2fd' : '#fafafa',
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
        '&:hover': { backgroundColor: '#f5f5f5' },
        cursor: isDragging ? 'grabbing' : 'grab',
        alignItems: 'flex-start',
        py: 1.5,
      }}
    >
      <ListItemIcon sx={{ minWidth: 40, mt: 1 }} {...attributes} {...listeners}>
        <DragIndicator sx={{ color: 'action.active' }} />
      </ListItemIcon>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: tier.color || '#ccc',
            }}
          />
          <TextField
            type="color"
            value={tier.color || '#808080'}
            onChange={(e) => onUpdate(tier.id, { color: e.target.value })}
            size="small"
            sx={{ width: 48, height: 36, p: 0.25, minWidth: 48 }}
            inputProps={{
              style: { height: 28, cursor: 'pointer', padding: 0 },
            }}
          />
        </Box>
        <TextField
          label="Label"
          value={tier.label || ''}
          onChange={(e) => onUpdate(tier.id, { label: e.target.value })}
          size="small"
          error={Boolean(labelError)}
          helperText={Array.isArray(labelError) ? labelError[0] : labelError}
          placeholder="e.g. Gold"
          sx={{ minWidth: 120 }}
        />
        <TextField
          label="Name"
          value={tier.name || ''}
          onChange={(e) => onUpdate(tier.id, { name: e.target.value }, { autoDeriveName: false })}
          size="small"
          error={Boolean(nameError)}
          helperText={Array.isArray(nameError) ? nameError[0] : nameError}
          placeholder="e.g. GOLD"
          sx={{ minWidth: 120 }}
        />
        <TextField
          label="Min. Orders"
          type="number"
          inputProps={{ min: 0, step: 1 }}
          value={tier.minOrders ?? ''}
          onChange={(e) =>
            onUpdate(tier.id, { minOrders: Math.max(0, parseInt(e.target.value, 10) || 0) })
          }
          size="small"
          sx={{ width: 110 }}
        />
        <TextField
          label="Min. Spend"
          type="number"
          inputProps={{ min: 0, step: 1 }}
          value={tier.minSpent ?? ''}
          onChange={(e) =>
            onUpdate(tier.id, { minSpent: Math.max(0, parseFloat(e.target.value) || 0) })
          }
          size="small"
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
          }}
          sx={{ width: 120 }}
        />
        <IconButton
          aria-label="Delete tier"
          onClick={() => onRemove(tier.id)}
          disabled={!canDelete}
          size="small"
          color="error"
        >
          <DeleteIcon />
        </IconButton>
      </Box>
    </ListItem>
  );
}

export default TierRow;
